<template>
  <!-- Banner para iOS -->
  <div v-if="showIOSBanner" class="ios-install-banner">
    <div class="banner-content">
      <div class="banner-info">
        <span class="app-icon">📱</span>
        <div class="banner-text">
          <strong class="h2-defaultMuni">Instala nuestra app</strong>
          <span class="h2-defaultMuni">Para una mejor experiencia</span>
        </div>
      </div>
      <div class="banner-actions">
        <button
          @click="showIOSInstructions = true"
          class="install-btn h2-defaultMuni"
        >
          Instalar
        </button>
        <button
          @click="dismissBanner('ios')"
          class="close-btn"
          title="No gracias"
        >
          <i class="bi bi-x-circle"></i>
        </button>
      </div>
    </div>

    <!-- Modal de instrucciones para iOS -->
    <div
      v-if="showIOSInstructions"
      class="instructions-modal"
      @click="showIOSInstructions = false"
    >
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showIOSInstructions = false">
          ×
        </button>
        <h3>Instalar en iPhone/iPad</h3>
        <div class="instructions-steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              <p>
                Toca el botón <strong>Compartir</strong>
                <span class="icon">📤</span>
              </p>
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              <p>
                Desplaza hacia abajo y selecciona
                <strong>"Agregar a pantalla de inicio"</strong>
              </p>
            </div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              <p>
                Presiona <strong>"Agregar"</strong> en la esquina superior
                derecha
              </p>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button @click="dismissBanner('ios')" class="dismiss-btn">
            No volver a mostrar
          </button>
          <button @click="showIOSInstructions = false" class="got-it-btn">
            ¡Entendido!
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Botón flotante para Android -->
  <button
    v-if="showAndroidButton"
    @click="triggerInstallPrompt"
    class="install-button"
  >
    <span class="button-icon"><i class="bi bi-download"></i></span>
    Instalar App
    <span
      v-if="showAndroidButton"
      @click.stop="dismissBanner('android')"
      title="No gracias"
      ><i class="bi bi-x-circle"></i
    ></span>
  </button>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";

// Refs para el estado
const installPromptEvent = ref(null);
const showIOSBanner = ref(false);
const showIOSInstructions = ref(false);

// Detección de plataforma
const isIOS = ref(
  /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream,
);
const isStandalone = ref(
  window.matchMedia("(display-mode: standalone)").matches,
);

// Estado de banners descartados
const dismissedBanners = ref({
  ios:
    localStorage.getItem("pwaBannerDismissed") === "true" ||
    localStorage.getItem("pwaBannerDismissed_ios") === "true",
  android:
    localStorage.getItem("pwaBannerDismissed") === "true" ||
    localStorage.getItem("pwaBannerDismissed_android") === "true",
});

// Computed para controlar la visibilidad
const showAndroidButton = computed(() => {
  return (
    installPromptEvent.value &&
    !isIOS.value &&
    !isStandalone.value &&
    !dismissedBanners.value.android
  );
});

// 1. Función que captura el evento del navegador (Android/Chrome)
const captureInstallPrompt = (event) => {
  event.preventDefault();
  console.log("PWA: Evento 'beforeinstallprompt' capturado.");
  installPromptEvent.value = event;
};

// 2. Función para Android - se llama al hacer clic en el botón
const triggerInstallPrompt = async () => {
  if (!installPromptEvent.value) {
    console.log("PWA: El prompt de instalación no está disponible.");
    return;
  }

  installPromptEvent.value.prompt();
  const { outcome } = await installPromptEvent.value.userChoice;

  if (outcome === "accepted") {
    console.log("PWA: El usuario aceptó la instalación.");
    // Limpiar el evento después de instalación exitosa
    installPromptEvent.value = null;
  } else {
    console.log("PWA: El usuario canceló la instalación.");
    // No descartamos automáticamente, dejamos que el usuario decida con el botón ×
  }
};

// 3. Función unificada para descartar banners
const dismissBanner = (platform) => {
  console.log(`PWA: Banner ${platform} descartado por el usuario`);

  if (platform === "ios") {
    showIOSBanner.value = false;
    showIOSInstructions.value = false;
  }

  // Marcar como descartado para la plataforma específica
  dismissedBanners.value[platform] = true;

  // Guardar en localStorage
  localStorage.setItem(`pwaBannerDismissed_${platform}`, "true");

  // También guardar un flag global para compatibilidad
  localStorage.setItem("pwaBannerDismissed", "true");

  // Si es Android, también limpiamos el evento
  if (platform === "android") {
    installPromptEvent.value = null;
  }
};

// 4. Verificar elegibilidad para mostrar banners
const checkInstallEligibility = () => {
  // No mostrar nada si ya está instalado
  if (isStandalone.value) {
    console.log("PWA: Ya está en modo standalone, no mostrar banners");
    return;
  }

  // Verificar para iOS
  if (isIOS.value && !dismissedBanners.value.ios) {
    console.log("PWA: Mostrando banner para iOS");
    // Esperar un poco antes de mostrar para no molestar inmediatamente
    setTimeout(() => {
      showIOSBanner.value = true;
    }, 3000);
  }

  // Para Android, el botón se controla mediante la computed property
  // basada en installPromptEvent y dismissedBanners
};

// 5. Reiniciar preferencias (útil para testing)
const resetBannerPreferences = () => {
  dismissedBanners.value = { ios: false, android: false };
  localStorage.removeItem("pwaBannerDismissed");
  localStorage.removeItem("pwaBannerDismissed_ios");
  localStorage.removeItem("pwaBannerDismissed_android");
  console.log("PWA: Preferencias de banner reiniciadas");
};

// 6. Lifecycle hooks
onMounted(() => {
  // Registrar listener para Android
  window.addEventListener("beforeinstallprompt", captureInstallPrompt);

  // Verificar elegibilidad para mostrar banners
  checkInstallEligibility();

  // También verificar cuando cambia la visibilidad de la página
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) {
      checkInstallEligibility();
    }
  });

  // Para debugging (opcional)
  if (process.env.NODE_ENV === "development") {
    window.resetPWAInstall = resetBannerPreferences;
    console.log("PWA: Para reiniciar preferencias ejecuta: resetPWAInstall()");
  }
});

// 7. Limpiar listeners
onBeforeUnmount(() => {
  window.removeEventListener("beforeinstallprompt", captureInstallPrompt);
  document.removeEventListener("visibilitychange", checkInstallEligibility);
});
</script>

<style scoped>
/* Banner para iOS */
.ios-install-banner {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #90eae0 0%, #00c3a8 100%);
  color: #3e3e3e;
  padding: 12px 16px;
  box-shadow: 0 -2px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideUp 0.3s ease-out;
}

.banner-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
}

.banner-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.app-icon {
  font-size: 24px;
}

.banner-text {
  display: flex;
  flex-direction: column;
}

.banner-text strong {
  font-size: 14px;
}

.banner-text span {
  font-size: 12px;
  opacity: 0.9;
}

.banner-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.install-btn {
  background: linear-gradient(180deg, #9e9e9e 0%, #676768 100%);
  color: var(--blanco);
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s;
}

.install-btn:active {
  transform: scale(0.95);
}

.close-btn {
  background: linear-gradient(180deg, #9e9e9e 0%, #676768 100%);
  border: none;
  color: var(--blanco);
  cursor: pointer;
  /* width: 30px;
  height: 30px; */
  height: 100%;
  aspect-ratio: 1/1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Modal de instrucciones iOS */
.instructions-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  animation: fadeIn 0.3s ease-out;
}

.modal-content {
  background: var(--blanco);
  color: #333;
  padding: 24px;
  border-radius: 16px;
  max-width: 400px;
  margin: 20px;
  text-align: center;
  position: relative;
  animation: scaleIn 0.3s ease-out;
}

.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.modal-content h3 {
  margin: 0 0 20px 0;
  color: #333;
  font-size: 20px;
}

.instructions-steps {
  text-align: left;
  margin: 24px 0;
}

.step {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
}

.step-number {
  background: var(----color-success);
  color: var(--blanco);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-content p {
  margin: 0;
  line-height: 1.4;
}

.icon {
  font-size: 18px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 20px;
}

.dismiss-btn {
  background: #f8f9fa;
  color: #666;
  border: 1px solid #ddd;
  padding: 10px 16px;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.dismiss-btn:hover {
  background: #e9ecef;
}

.got-it-btn {
  background: linear-gradient(180deg, #9e9e9e 0%, #676768 100%);
  color: var(--blanco);
  border: none;
  padding: 10px 24px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  transition: transform 0.2s;
}

.got-it-btn:active {
  transform: scale(0.95);
}

/* Botón para Android */
.install-button {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;

  background: linear-gradient(
    135deg,
    var(--color-success) 0%,
    var(--color-success) 100%
  );
  color: var(--white);
  padding: 12px 20px;
  border-radius: 25px;
  border: none;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 195, 168, 0.3);
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  animation: bounceIn 0.5s ease-out;
}

.install-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 195, 168, 0.4);
}

.install-button:active {
  transform: translateY(0);
}

/* Botón de cierre para Android */
.android-close-btn {
  position: fixed;
  bottom: 80px;
  right: 25px;
  z-index: 1001;

  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: none;
  color: #666;
  font-size: 18px;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  animation: fadeIn 0.5s ease-out 0.3s both;
}

.android-close-btn:hover {
  background: var(--blanco);
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.button-icon {
  font-size: 16px;
}

/* Animaciones */
@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes bounceIn {
  0% {
    opacity: 0;
    transform: scale(0.3) translateY(20px);
  }
  50% {
    opacity: 1;
    transform: scale(1.05) translateY(-5px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Responsive */
@media (max-width: 480px) {
  .banner-content {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }

  .banner-info {
    justify-content: center;
  }

  .install-button {
    bottom: 16px;
    right: 16px;
    padding: 10px 16px;
    font-size: 13px;
  }

  .android-close-btn {
    bottom: 70px;
    right: 21px;
  }

  .modal-content {
    margin: 16px;
    padding: 20px;
  }

  .modal-actions {
    flex-direction: column;
  }
}
</style>
